/**
 * integrationAddOns.ts
 *
 * Add-on / prerequisite logic for integration catalogs. Some integrations sell
 * items that can only be used together with a required device — e.g. an Axiom
 * peptide can only be administered with an injection "pen". The pen is modeled
 * as its own catalog item flagged `isAddOn`, and the peptides that need it are
 * flagged `requiresAddOn` (both set per-item by the Corp super-admin).
 *
 * Until those flags are configured, we fall back to a name match so the gate
 * still works on day one for the canonical "pen" case.
 */
import type { IntegrationProduct } from '@/services/integrations.service';

/** Integration key of the Axiom vials module — the second half of a combined pens + vials cart. */
export const AXIOM_VIALS_KEY = 'axiom_vials';

/** Name/key fallback used when `metadata.isAddOn` hasn't been set in Corp. */
const ADD_ON_NAME_RX = /\bpens?\b/i;

// ── Catalog categories (vials module) ────────────────────────────────────────

/** Normalized catalog section of a product ('' on flat/pens catalogs). */
export const categoryOf = (p: IntegrationProduct): string =>
  String(p.metadata?.category ?? '').trim().toUpperCase();

/**
 * SHIPPING-category items are a FEE, not something staff pick: the server
 * rejects orders without exactly one shipping unit (SHIPPING_REQUIRED /
 * SHIPPING_DUPLICATE), and the modal adds that one unit itself — see
 * defaultShippingProduct(). There is no shipping picker (SCRUM-2023).
 */
export const isShippingProduct = (p: IntegrationProduct): boolean => categoryOf(p) === 'SHIPPING';

/**
 * The sku of the one general shipping fee (SCRUM-2023). It is the former Cold
 * row ($25 in Exhibit B): `axiom:consolidate-vials-shipping` keeps that row,
 * renames it "Shipping" and deactivates the others, so preferring it while the
 * data still holds both rows already bills $25, not the Standard row's price.
 */
export const DEFAULT_SHIPPING_SKU = 'AXM-SHIP-COLD';

/** What every shipping fee line is called, wherever staff or the patient see it. */
export const SHIPPING_LABEL = 'Shipping';

/**
 * The name an order line is shown under. A shipping fee line reads "Shipping",
 * never its catalog name ("Shipping — Standard (non-cold), per order"): the
 * pharmacy decides cold or not, so the order must not claim either (SCRUM-2023).
 * Recognised by its catalog category when the line carries it, else by a name
 * starting with "Shipping" (older lines and integration_order_items rows).
 */
export function orderLineName(name: string, productMetadata?: { category?: unknown } | null): string {
  const category = String(productMetadata?.category ?? '').trim().toUpperCase();
  if (category === 'SHIPPING' || /^shipping\b/i.test(String(name ?? '').trim())) return SHIPPING_LABEL;
  return name;
}

/**
 * The ONE shipping fee a vials order carries. SCRUM-2023: one general fee,
 * the pharmacy decides cold vs not cold, nobody picks. Normally the catalog
 * has a single active SHIPPING row and that is it; while the data still holds
 * several (Standard + Cold), prefer DEFAULT_SHIPPING_SKU, else the lowest id
 * so the choice is stable. null when the catalog has no SHIPPING row (pens).
 */
export function defaultShippingProduct<T extends IntegrationProduct>(products: T[]): T | null {
  const shipping = products.filter(isShippingProduct);
  if (shipping.length === 0) return null;
  const bySku = shipping.find(
    (p) => String(p.metadata?.sku ?? '').trim().toUpperCase() === DEFAULT_SHIPPING_SKU,
  );
  if (bySku) return bySku;
  return shipping.reduce((lo, p) => (p.integrationProductId < lo.integrationProductId ? p : lo));
}

/**
 * Catalog categories that are a peptide/GLP-1 VIAL (as opposed to a nasal
 * spray, a supply, or the SHIPPING row). Used to auto-seed insulin syringes
 * the first time ANY vial goes on the order (SUP-000799) — a "what kind of
 * product is this" question, not a shipping one (the pharmacy decides
 * packaging per product itself; we no longer force a shipping choice off
 * this list, see git history for the removed SHIPPING_COLD_REQUIRED rule).
 */
export const VIAL_CATEGORIES = ['PEPTIDE VIALS', 'GLP-1 VIALS'] as const;

/** True when this catalog item is a peptide / GLP-1 vial. */
export function isVialProduct(p: IntegrationProduct): boolean {
  return (VIAL_CATEGORIES as readonly string[]).includes(categoryOf(p));
}

/** True when this catalog item is itself the add-on device (e.g. the pen). */
export function isAddOnProduct(p: IntegrationProduct): boolean {
  const flag = p.metadata?.isAddOn;
  if (typeof flag === 'boolean') return flag;
  return ADD_ON_NAME_RX.test(p.name) || ADD_ON_NAME_RX.test(p.key);
}

/**
 * True when ordering this item requires the add-on to be present. Explicit
 * `metadata.requiresAddOn` wins; otherwise we fall back to "any non-add-on item
 * needs the add-on" whenever the catalog actually contains an add-on device.
 */
export function productRequiresAddOn(p: IntegrationProduct, catalog: IntegrationProduct[]): boolean {
  const flag = p.metadata?.requiresAddOn;
  if (typeof flag === 'boolean') return flag;
  return !isAddOnProduct(p) && catalog.some(isAddOnProduct);
}

/** The add-on device in this catalog, if any. */
export function findAddOnProduct(catalog: IntegrationProduct[]): IntegrationProduct | undefined {
  return catalog.find(isAddOnProduct);
}

/** The add-on's category — 'pen' | 'needles' (from metadata.addOnType). */
export function addOnType(p: IntegrationProduct): string | undefined {
  const t = p.metadata?.addOnType;
  return typeof t === 'string' ? t : undefined;
}

/** Add-on products of a given type. */
export function addOnsByType(catalog: IntegrationProduct[], type: string): IntegrationProduct[] {
  return catalog.filter((p) => isAddOnProduct(p) && addOnType(p) === type);
}

/**
 * Catalog key of the bundled pen, used when `metadata.penBundle` hasn't been set.
 * The row deliberately carries `metadata.sku = 'AXM-PEN'` so the pharmacy is
 * told to ship the pen SKU it already fulfils — only the PRICE differs.
 */
const PEN_BUNDLE_KEY = 'AXM-PEN-BUNDLE';

/**
 * True when this item is the BUNDLED pen — the one added automatically for each
 * cartridge ordered from the "Pen + Cartridge" list, priced as the bundle
 * premium rather than the standalone device.
 *
 * It is a distinct catalog row from the standalone pen so the two can be priced
 * independently (the standalone pen keeps its own retail price). Both are
 * `addOnType: 'pen'`, so anything that wants *the* pen must disambiguate.
 */
export function isPenBundleProduct(p: IntegrationProduct): boolean {
  if (!isAddOnProduct(p)) return false;
  if (typeof p.metadata?.penBundle === 'boolean') return p.metadata.penBundle;
  return p.key === PEN_BUNDLE_KEY;
}

/** The bundled-pen add-on, if this catalog has one (pens module only). */
export function findPenBundleAddOn(catalog: IntegrationProduct[]): IntegrationProduct | undefined {
  return catalog.find(isPenBundleProduct);
}

/**
 * The single STANDALONE pen add-on (falls back to the legacy name match).
 * Excludes the bundled pen: both are `addOnType: 'pen'`, and picking the bundle
 * here would price a standalone pen purchase at the bundle premium.
 */
export function findPenAddOn(catalog: IntegrationProduct[]): IntegrationProduct | undefined {
  return addOnsByType(catalog, 'pen').find((p) => !isPenBundleProduct(p))
    ?? catalog.find((p) => isAddOnProduct(p) && !isPenBundleProduct(p) && /\bpens?\b/i.test(p.name));
}

/**
 * True when this catalog item IS the reusable injector pen — the durable device
 * a patient buys once and keeps, as opposed to the consumables (cartridges,
 * needle tips) they re-order. `metadata.addOnType === 'pen'` is the
 * discriminator (needles carry `addOnType: 'needles'` and are NOT pens); the
 * name match only applies to add-ons whose type was never configured, mirroring
 * findPenAddOn's fallback.
 *
 * Deliberately TRUE for the bundled pen as well: the callers of this predicate
 * ask "is this a pen the patient already owns?" — which a re-order must drop
 * whether it arrived standalone or bundled with a cartridge.
 */
export function isPenProduct(p: IntegrationProduct): boolean {
  if (!isAddOnProduct(p)) return false;
  const t = addOnType(p);
  return t === 'pen' || (t === undefined && /\bpens?\b/i.test(p.name));
}

/** Needle-tip add-on packs, with the configured default (metadata.addOnDefault) first. */
export function needleAddOns(catalog: IntegrationProduct[]): IntegrationProduct[] {
  return [...addOnsByType(catalog, 'needles')].sort(
    (a, b) => (b.metadata?.addOnDefault ? 1 : 0) - (a.metadata?.addOnDefault ? 1 : 0),
  );
}

/**
 * The per-cartridge PREMIUM needle upgrade. Standard needles ship with each
 * cartridge (free); choosing Premium adds one of these per peptide cartridge.
 * Flagged metadata.premiumUpgrade; falls back to the AXM-PREM-TIP25 key.
 */
export function premiumNeedleUpgrade(catalog: IntegrationProduct[]): IntegrationProduct | undefined {
  return addOnsByType(catalog, 'needles').find((p) => p.metadata?.premiumUpgrade === true)
    ?? catalog.find((p) => p.key === 'AXM-PREM-TIP25');
}

/** Human label for the add-on ("pen"), preferring configured metadata. */
export function addOnLabel(
  requiredItem: IntegrationProduct | undefined,
  addOn: IntegrationProduct | undefined,
): string {
  return (
    requiredItem?.metadata?.addOnLabel ||
    addOn?.metadata?.addOnLabel ||
    addOn?.name ||
    'pen'
  );
}
